import axios from "axios";

import smartStorage from "./smart-storage";
import eventBus from "./event-bus";
import { isEuRegion } from "./regions";
import { getQueryParam } from "./url";
import { getBundleId } from "./auth";
import { getUserAgentHeader } from "./userAgent";

/**
 * Function to return BLL base url
 * this was added support dynamic bll base
 * @returns bll url
 */
export function getBLLBaseURL() {
  const qBLLBaseURL = getQueryParam("bll_base_url");
  let baseUrl = process.env.VUE_APP_BLL_BASE_URL;

  if (isEuRegion) {
    smartStorage.setItem("bll_base_url", decodeURIComponent(process.env.VUE_APP_EU_API_URL));
    baseUrl = process.env.VUE_APP_EU_API_URL;
  }

  if (qBLLBaseURL) {
    smartStorage.setItem("bll_base_url", decodeURIComponent(qBLLBaseURL));
    baseUrl = qBLLBaseURL;
  }

  return baseUrl;
}

export function buildQueryParamsFromList(list, param) {
  return list.reduce((acc, e, i) => `${acc}${i ? "&" : ""}${param}=${e}`, "");
}

/**
 * Appends the common query parameters to requests.
 * @returns The `params` object which contains all common query params
 */
export function getCommonParams() {
  const params = {};
  const bundleId = getBundleId();

  if (bundleId) params["bundleId"] = bundleId;

  return params;
}

const baseUrl = getBLLBaseURL(); // FIXME remove temp BASE_URL

/**
 * Retry policy for transient failures. Opt-in per request via `retry` on the config:
 * - `retry: true` enables retries with the defaults below.
 * - `retry: { retries, retryableStatuses }` tunes them.
 * - Omitted / falsy => no retry.
 * Only enable it for idempotent requests (GETs) or side-effect-free calculations
 * (e.g. calculate-charge, which is a POST but computes a price without mutating state).
 */
const DEFAULT_MAX_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 300;
// Transient/retryable: request timeout, too-early, rate-limit, and gateway/5xx errors.
const RETRYABLE_STATUSES = [408, 425, 429, 500, 502, 503, 504];

function resolveRetryConfig(config = {}) {
  const optIn = config.retry;
  if (!optIn) return null; // retry is strictly opt-in

  const override = typeof optIn === "object" ? optIn : {};
  return {
    retries: override.retries ?? DEFAULT_MAX_RETRIES,
    retryableStatuses: override.retryableStatuses ?? RETRYABLE_STATUSES,
  };
}

function isRetryableError(error, retryableStatuses) {
  // No response => network/timeout/CORS-abort => retryable.
  if (!error?.response) return true;
  return retryableStatuses.includes(error.response.status);
}

function getRetryDelay(attempt) {
  // Exponential backoff with a little jitter: ~300ms, ~600ms, ...
  return RETRY_BASE_DELAY_MS * 2 ** (attempt - 1) + Math.floor(Math.random() * 100);
}

const $http = axios.create({
  baseURL: baseUrl,
});

$http.interceptors.request.use(function insertHeader(config) {
  config.headers = {
    "X-Auth": "controlPanel",
    "X-User-Agent": getUserAgentHeader(),
    ...config.headers,
  };

  const authToken = smartStorage.authToken;
  if (authToken) {
    config.headers["Authorization"] = authToken;
  }
  config.params = {
    ...getCommonParams(),
    ...config.params,
  };

  return config;
});

$http.interceptors.response.use(
  /* Send the actual data to then() */
  response => response?.data,
  async function (error) {
    const config = error?.config || {};
    const retryConfig = resolveRetryConfig(config);

    // Retry transient failures (network errors + retryable statuses) with backoff.
    if (retryConfig && isRetryableError(error, retryConfig.retryableStatuses)) {
      config.__retryCount = config.__retryCount || 0;
      if (config.__retryCount < retryConfig.retries) {
        config.__retryCount += 1;
        await new Promise(resolve => setTimeout(resolve, getRetryDelay(config.__retryCount)));
        return $http(config);
      }
    }

    const { response } = error;

    if (!response) {
      console.log("client side network error");
      return;
    }

    if (response.status === 401) {
      // Whenever "go-to-login" is emitted, wwe clear the storage and take the user to the login page.
      eventBus.$emit("go-to-login");
    }

    return Promise.reject(response);
  }
);

export default $http;
export function http() {
  this.axios = axios.create({
    baseURL: baseUrl,
  });
}

http.prototype.getData = function (
  url,
  headers,
  successCallback = () => {},
  errorCallback = () => {},
  params = {}
) {
  return this.axios
    .get(url, {
      ...headers,
      params: params,
    })
    .then(({ data }) => {
      successCallback(data);

      return data;
    })
    .catch(data => {
      var response = data.response;
      errorCallback(response);
      if (!response) {
        console.log("client side network error");
        return;
      }
      if (response.status === 401) {
        eventBus.$emit("go-to-login");
      }

      return data;
    });
};

http.prototype.postData = function (
  url,
  params,
  headers = {},
  successCallback = () => {},
  errorCallback = () => {}
) {
  return this.axios
    .post(url, params, headers)
    .then(({ data }) => {
      successCallback(data);
      return data;
    })
    .catch(data => {
      var response = data.response;
      errorCallback(response);
      if (!response) {
        console.log("client side network error");
        return;
      }
      if (response.status === 401) {
        eventBus.$emit("go-to-login");
      }

      return data;
    });
};
